import Vue from 'vue';
import { Commit, ActionContext } from 'vuex';

import { RuleApplicationParams, AppliedRule, Rule, RuleParams } from '@/interfaces/rules';

interface AppliedRuleState {
	appliedRules: {
		[id: number]: AppliedRule;
	};
	appliedInternalRules: {
		[id: number]: AppliedRule;
	};
}

export default {
	namespaced: true,
	state: {
		appliedRules: {},
		appliedInternalRules: {},
	} as AppliedRuleState,
	getters: {
		getAppliedRules: (state: AppliedRuleState): AppliedRule[] => {
			return Object.values(state.appliedRules);
		},
		getAppliedInternalRules: (state: AppliedRuleState): AppliedRule[] => {
			return Object.values(state.appliedInternalRules);
		},
		getAppliedRule:
			(state: AppliedRuleState) =>
			(id: number): AppliedRule | undefined => {
				return state.appliedRules[id];
			},
		getDeviceAppliedRule:
			(state: AppliedRuleState) =>
			(deviceId: number, ruleId: number): AppliedRule | undefined => {
				return Object.values(state.appliedRules).find(
					(appliedRule: AppliedRule) =>
						appliedRule.fk_device === deviceId && appliedRule.fk_rule === ruleId
				);
			},
	},
	mutations: {
		SET_APPLIED_RULES(state: AppliedRuleState, appliedRules: AppliedRule[]): void {
			const ret: { [id: number]: AppliedRule } = {};
			state.appliedRules = appliedRules.reduce((obj, appliedRule) => {
				obj[appliedRule.id] = appliedRule;
				return obj;
			}, ret);
		},
		SET_APPLIED_INTERNAL_RULES(state: AppliedRuleState, appliedRules: AppliedRule[]): void {
			const ret: { [id: number]: AppliedRule } = {};
			state.appliedInternalRules = appliedRules.reduce((obj, appliedRule) => {
				obj[appliedRule.id] = appliedRule;
				return obj;
			}, ret);
		},

		ADD_APPLIED_RULE(state: AppliedRuleState, appliedRule: AppliedRule): void {
			Vue.set(state.appliedRules, appliedRule.id, appliedRule);
		},

		CHANGE_APPLIED_RULE(state: AppliedRuleState, appliedRule: AppliedRule & { id: number }): void {
			const oldAppliedRule = state.appliedRules[appliedRule.id] || {};
			Vue.set(state.appliedRules, appliedRule.id, { ...oldAppliedRule, ...appliedRule });
		},

		CLEAN_APPLIED_RULE(state: AppliedRuleState): void {
			state.appliedRules = {};
		},
		CLEAN_APPLIED_INTERNAL_RULE(state: AppliedRuleState): void {
			state.appliedInternalRules = {};
		},
		REMOVE_APPLIED_INTERNAL_RULE(state: AppliedRuleState, appliedRuleId: number): void {
			Vue.delete(state.appliedInternalRules, appliedRuleId);
		},
		REMOVE_APPLIED_RULE(state: AppliedRuleState, appliedRuleId: number): void {
			Vue.delete(state.appliedRules, appliedRuleId);
		},
	},
	actions: {
		async fetchAppliedRules({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/rules/applied`);

			if (response && response.status === 200) {
				commit('SET_APPLIED_RULES', response.data.data);
			}
		},

		async fetchAppliedInternalRules({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/rules/applied`, {
				params: { internal: true },
			});

			if (response && response.status === 200) {
				commit('SET_APPLIED_INTERNAL_RULES', response.data.data);
			}
		},

		async addAppliedRuleGroup(
			{ commit }: ActionContext<AppliedRuleState, unknown>,
			{ params }: { params: RuleApplicationParams }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/rules/applied/group`,
				params
			);
			if (response && response.status === 200) {
				for (const appliedRule of response.data.data.applieds) {
					commit('ADD_APPLIED_RULE', appliedRule);
				}
				commit('activities/rules/CHANGE_RULE', response.data.data.rule, { root: true });
			}
		},
		async addAppliedRule(
			{ dispatch }: ActionContext<AppliedRuleState, unknown>,
			{ params }: { params: RuleParams }
		): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/rules/applied`, params);
			if (response && response.status === 201) {
				await dispatch('fetchAppliedRule', { appliedRuleId: response.data.data });
			}
		},

		async fetchAppliedRule(
			{ commit }: { commit: Commit },
			{ appliedRuleId }: { appliedRuleId: number }
		): Promise<void> {
			const response = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/rules/applied/${appliedRuleId}`
			);
			if (response && response.status === 200) {
				commit('ADD_APPLIED_RULE', response.data.data);
			}
		},
		async removeAppliedRuleGroup(
			{ commit, rootGetters }: ActionContext<AppliedRuleState, unknown>,
			{ zoneId }: { zoneId: number }
		): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/rules/applied/group/${zoneId}`
			);
			if (response && response.status === 200) {
				const rule: Rule = rootGetters['activities/rules/getRuleByGroup'](zoneId);
				if (typeof rule !== 'undefined') {
					commit('activities/rules/REMOVE_RULE', rule.id, { root: true });
				}
			}
		},

		async removeAppliedRule(
			{ commit }: ActionContext<AppliedRuleState, unknown>,
			{ id }: { id: number }
		): Promise<void> {
			const response = await Vue.axios.delete(`${process.env.VUE_APP_API_URL}/rules/applied/${id}`);
			if (response && response.status === 200) {
				commit('REMOVE_APPLIED_RULE', id);
			}
		},

		cleanAppliedRule({ commit }: { commit: Commit }): void {
			commit('CLEAN_APPLIED_RULE');
		},
		cleanAppliedInternalRule({ commit }: { commit: Commit }): void {
			commit('CLEAN_APPLIED_INTERNAL_RULE');
		},
	},
};
